import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory('/'),
  routes: [
    // Public auth flows — no JWT required
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { public: true, standalone: true },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/ForgotPasswordView.vue'),
      meta: { public: true, standalone: true },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/ResetPasswordView.vue'),
      meta: { public: true, standalone: true },
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      component: () => import('@/views/VerifyEmailView.vue'),
      meta: { public: true, standalone: true },
    },
    // Public anonymous pet-ID-tag designer. The :token is a per-tenant
    // capability token from an exam-room QR; no JWT, no nav chrome. Holds the
    // token in memory only (never mints a JWT or writes localStorage).
    {
      path: '/design/:token',
      name: 'design-tag',
      component: () => import('@/views/DesignTagView.vue'),
      meta: { public: true, standalone: true },
    },

    // Authenticated app
    {
      path: '/',
      name: 'dashboard',
      component: () => import('@/views/DashboardView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/archive',
      name: 'archive',
      component: () => import('@/views/ArchiveView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/new',
      name: 'new-project',
      component: () => import('@/views/NewProjectView.vue'),
      meta: { requiresAuth: true },
    },
    // The per-product intake routes are gone: /new is one screen for every
    // product, with ?kind= / ?style= preselecting its controls. These redirects
    // keep bookmarks and any link still in the wild landing on the same form
    // the customer expected; drop them a release after the deploy.
    {
      path: '/new-fast',
      redirect: () => ({ path: '/new', query: { kind: 'paw', style: 'fast' } }),
    },
    {
      path: '/new-nose',
      redirect: (to) => ({
        path: '/new',
        query: { kind: 'nose', style: to.query.style === 'line' ? 'line' : 'photo' },
      }),
    },
    // Staff-side pet ID tag designer. Mounts the same DesignTagView the pet
    // owner gets from the exam-room QR, with the tenant's own token resolved by
    // the wrapper. `feature` keeps a non-internal tenant off the route even by
    // direct URL, matching the gateway's 403.
    {
      path: '/new-tag',
      name: 'new-tag',
      component: () => import('@/views/StaffTagDesignView.vue'),
      meta: { requiresAuth: true, feature: 'tag_designer' },
    },
    // Logo engrave. Its own route for the same reason the tag designer has one:
    // no pet, no photo, no species — the art is the clinic's stored logo, so
    // folding it into /new would mean a product control that blanks the form.
    // `feature` keeps a non-internal tenant off the route even by direct URL,
    // matching the gateway's 403.
    {
      path: '/new-logo',
      name: 'new-logo',
      component: () => import('@/views/LogoEngraveView.vue'),
      meta: { requiresAuth: true, feature: 'logo_engrave' },
    },
    {
      path: '/project/:id',
      name: 'frame-photo',
      component: () => import('@/views/FramePhotoView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/project/:id/nose-frame',
      name: 'nose-frame',
      component: () => import('@/views/NoseFrameView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/project/:id/nose-text',
      name: 'nose-text',
      component: () => import('@/views/NoseTextView.vue'),
      meta: { requiresAuth: true },
    },
    // Landing page for the "Pick your favorite" button in a variant review
    // email. requiresAuth sends a signed-out visitor to /login?redirect=, and
    // LoginView returns them here after sign-in.
    {
      path: '/project/:id/pick',
      name: 'pick-variant',
      component: () => import('@/views/PickVariantView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/project/:id/align',
      name: 'align-art',
      component: () => import('@/views/AlignArtView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/project/:id/text',
      name: 'text-overlay',
      component: () => import('@/views/TextOverlayView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/project/:id/preview',
      name: 'preview',
      component: () => import('@/views/PreviewView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/project/:id/engrave',
      name: 'engrave',
      component: () => import('@/views/EngraveView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/project/:id/fulfill',
      name: 'fulfill',
      component: () => import('@/views/FulfillView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/account',
      name: 'account',
      component: () => import('@/views/AccountView.vue'),
      meta: { requiresAuth: true },
    },
    // The way to add an engraver: a stock engraver from Vetware joins the
    // account during its WiFi setup, by claim code.
    {
      path: '/setup',
      name: 'setup',
      component: () => import('@/views/AddEngraverView.vue'),
      meta: { requiresAuth: true },
    },
    // Flashing your own AtomStack over USB (WebSerial).
    {
      path: '/setup/usb',
      name: 'setup-usb',
      component: () => import('@/views/SetupView.vue'),
      meta: { requiresAuth: true },
    },
    // Shipped briefly as the claim flow's path before it became /setup.
    { path: '/add-engraver', redirect: '/setup' },
    {
      path: '/engraver/:id',
      name: 'engraver',
      component: () => import('@/views/EngraverView.vue'),
      meta: { requiresAuth: true },
    },
    // Not nested under /engraver/:id: the WiFi change is a property of the
    // engraver on the USB cable, not of a record. The page reads the device's
    // own id off its boot log and reports it back.
    {
      path: '/engraver-wifi',
      name: 'engraver-wifi',
      component: () => import('@/views/EngraverWifiView.vue'),
      meta: { requiresAuth: true },
    },
    // The engraver-scoped path shipped first. Keep it resolving so a bookmark
    // or an open tab from that build doesn't land on the 404.
    {
      path: '/engraver/:id/wifi',
      redirect: { name: 'engraver-wifi' },
    },

    // Catch-all 404. Marked public+standalone so it renders for both
    // authenticated and unauthenticated users without bouncing through the
    // login redirect (otherwise a logged-out user hitting a typo'd path
    // would just be redirected to /login with no clue what went wrong).
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue'),
      meta: { public: true, standalone: true },
    },
  ],
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  if (auth.token && !auth.user) {
    await auth.fetchMe()
  }

  if (to.meta.public) {
    if (auth.isAuthenticated && (to.name === 'login' || to.name === 'forgot-password')) {
      return { name: 'dashboard' }
    }
    return true
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  // Feature-gated routes: a tenant whose channel doesn't grant the feature is
  // sent to the dashboard rather than shown a view whose API calls all 403.
  const feature = to.meta.feature as string | undefined
  if (feature && !auth.user?.features?.[feature]) {
    return { name: 'dashboard' }
  }

  return true
})

export default router
